import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Check, SlidersHorizontal, Users, Monitor, LayoutGrid, ArrowRight, Phone, Mail, Star, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import Footer from "./Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const phoneCodes = [
  { code: "+91", flag: "\u{1F1EE}\u{1F1F3}", label: "India" }, { code: "+1", flag: "\u{1F1FA}\u{1F1F8}", label: "USA" },
  { code: "+44", flag: "\u{1F1EC}\u{1F1E7}", label: "UK" }, { code: "+971", flag: "\u{1F1E6}\u{1F1EA}", label: "UAE" },
  { code: "+65", flag: "\u{1F1F8}\u{1F1EC}", label: "Singapore" }, { code: "+61", flag: "\u{1F1E6}\u{1F1FA}", label: "Australia" },
  { code: "+49", flag: "\u{1F1E9}\u{1F1EA}", label: "Germany" }, { code: "+33", flag: "\u{1F1EB}\u{1F1F7}", label: "France" },
  { code: "+81", flag: "\u{1F1EF}\u{1F1F5}", label: "Japan" }, { code: "+86", flag: "\u{1F1E8}\u{1F1F3}", label: "China" },
  { code: "+82", flag: "\u{1F1F0}\u{1F1F7}", label: "South Korea" }, { code: "+60", flag: "\u{1F1F2}\u{1F1FE}", label: "Malaysia" },
  { code: "+66", flag: "\u{1F1F9}\u{1F1ED}", label: "Thailand" }, { code: "+27", flag: "\u{1F1FF}\u{1F1E6}", label: "South Africa" },
  { code: "+966", flag: "\u{1F1F8}\u{1F1E6}", label: "Saudi Arabia" },
];

const indianStates = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", "Goa", "Gujarat", "Haryana",
  "Himachal Pradesh", "Jharkhand", "Karnataka", "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur",
  "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu",
  "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal", "Delhi", "Jammu and Kashmir",
];

const SERVICE_DATA = {
  "fit": {
    icon: SlidersHorizontal,
    title: "FIT & Tailor-made",
    headline: "Custom Itineraries.\nBuilt for Your Clients.",
    subheadline: "Send us your client's brief. We'll deliver a fully costed, ready-to-sell itinerary with live rates — typically within 48 hours.",
    formTitle: "Get a Custom FIT Proposal",
    formSub: "Share your requirements. Our FIT team will respond with a detailed itinerary and pricing.",
    benefits: [
      "Fully customised itineraries across 25+ DMC destinations",
      "Live hotel rates with instant confirmation",
      "On-ground DMC teams handling every detail",
      "Dedicated account manager for high-value bookings",
      "White-label proposals you can share directly with clients",
      "48-hour turnaround on custom itinerary requests",
    ],
    stats: [{ val: "25+", label: "Destinations" }, { val: "48hr", label: "Turnaround" }, { val: "850+", label: "Agent Partners" }],
  },
  "groups": {
    icon: Users,
    title: "Groups · GIT & Ad Hoc",
    headline: "Group Travel,\nSimplified.",
    subheadline: "From guaranteed departures to ad hoc groups of any size — we handle the full ground operation so you can focus on selling.",
    formTitle: "Enquire About Group Services",
    formSub: "Tell us about your group requirements. We'll come back with routing, pricing, and availability.",
    benefits: [
      "Guaranteed departure series with fixed inventory and pricing",
      "Ad hoc group handling from 10 to 500+ travellers",
      "Full ground logistics: hotels, coaches, guides, meals",
      "Real-time group dashboard for tracking and payments",
      "Dedicated group coordinator for every departure",
      "Flexible cancellation and amendment policies for agents",
    ],
    stats: [{ val: "500+", label: "Groups / Year" }, { val: "10–500", label: "Pax Range" }, { val: "Zero", label: "Failed Departures" }],
  },
  "mice": {
    icon: Monitor,
    title: "MICE",
    headline: "Meetings. Incentives.\nConferences. Events.",
    subheadline: "From boardroom meetings to 1,000-guest gala dinners — our MICE team delivers end-to-end event management across Asia, the Middle East, and beyond.",
    formTitle: "Submit a MICE Brief",
    formSub: "Share your event details. We'll respond with venue options, logistics plan, and a full cost proposal.",
    benefits: [
      "End-to-end event management: venue, logistics, catering, entertainment",
      "Dedicated MICE project manager from brief to execution",
      "Venue sourcing across 25+ countries with negotiated rates",
      "Team-building activities, gala dinners, and cultural programmes",
      "Delegate registration, transfers, and on-site coordination",
      "Post-event reporting with detailed ROI metrics",
    ],
    stats: [{ val: "200+", label: "Events / Year" }, { val: "1,000+", label: "Max Capacity" }, { val: "30+", label: "Years Experience" }],
  },
  "agent-portal": {
    icon: LayoutGrid,
    title: "Agent Portal",
    headline: "Your Complete\nB2B Booking Platform.",
    subheadline: "Live inventory, real-time pricing, and instant confirmations across hotels, flights, experiences, and transfers — all in one platform built for travel agents.",
    formTitle: "Get Agent Portal Access",
    formSub: "Sign up for free. Our team will set up your account and walk you through the platform.",
    benefits: [
      "Live rates and instant confirmations on hotels, flights, and more",
      "Dynamic packaging: combine hotels, activities, and transfers",
      "White-label solution — your branding, our technology",
      "Real-time margin controls and cost visibility on every booking",
      "Manage bookings, amendments, and cancellations in one dashboard",
      "24/7 support from our dedicated agent success team",
    ],
    stats: [{ val: "200+", label: "Destinations" }, { val: "Real-time", label: "Inventory" }, { val: "24/7", label: "Support" }],
  },
};

const testimonials = [
  { name: "Vikram Malhotra", agency: "Malhotra World Travels", city: "New Delhi", quote: "Hi DMC's live rates saved us hours every day. We stopped calling suppliers for quotes — everything is instant now. Our booking volume tripled in 6 months.", rating: 5 },
  { name: "Priya Nair", agency: "Southern Star Holidays", city: "Kochi", quote: "The white-label solution is exactly what we needed. Our clients see our brand, but we get Hi DMC's 30 years of DMC quality behind every booking.", rating: 5 },
  { name: "Ankit Sharma", agency: "Sharma International Tours", city: "Jaipur", quote: "Best pricing I've found anywhere. The lowest price technology consistently beats what we were getting from our direct supplier contracts.", rating: 5 },
  { name: "Meera Krishnan", agency: "TravelCraft India", city: "Chennai", quote: "We used to take 2 days to build a Europe itinerary. With Hi DMC's platform, we do it in 20 minutes with live rates. Game changer for our agency.", rating: 5 },
  { name: "Rahul Deshmukh", agency: "Deshmukh Holidays", city: "Pune", quote: "The MICE module is brilliant. We handled a 300-pax incentive trip across 3 countries without a single manual spreadsheet. The platform scaled effortlessly.", rating: 5 },
  { name: "Sunita Agarwal", agency: "Agarwal Travel Services", city: "Kolkata", quote: "I was skeptical at first, but the support team walked us through everything. Within a week, we were booking faster than ever. The live rates are unbeatable.", rating: 5 },
  { name: "Deepak Patel", agency: "Gujarat Globe Trotters", city: "Ahmedabad", quote: "Our margins improved by 22% after switching to Hi DMC. The markup controls and cost visibility gave us complete clarity on every booking.", rating: 5 },
  { name: "Kavitha Reddy", agency: "Deccan Voyages", city: "Hyderabad", quote: "Ready-made itineraries saved our team so much time. We just customise and sell. Our agents close deals faster and clients love the quality.", rating: 5 },
  { name: "Arjun Singh", agency: "Royal Rajasthan Travels", city: "Udaipur", quote: "We've been with Hi DMC for 8 years. The platform keeps getting better. Live pricing, instant confirmations — it's like having a tech team without the cost.", rating: 5 },
  { name: "Neha Gupta", agency: "Gupta International", city: "Lucknow", quote: "The dynamic packaging feature lets us build unique experiences for each client. No two itineraries are the same, and pricing is always competitive.", rating: 5 },
];

const partnerLogos = [
  { name: "Marriott", src: "https://customer-assets.emergentagent.com/job_9a57b70a-7fda-4e0f-8fd3-cbc57f5e99da/artifacts/ql8ywhfb_Download%20225x225.png" },
  { name: "Hilton", src: "https://customer-assets.emergentagent.com/job_9a57b70a-7fda-4e0f-8fd3-cbc57f5e99da/artifacts/tvyx2nn7_Image%20225x225.png" },
  { name: "Emirates", src: "https://customer-assets.emergentagent.com/job_9a57b70a-7fda-4e0f-8fd3-cbc57f5e99da/artifacts/y7lxzjfa_Image%20218x231.png" },
  { name: "Expedia", src: "https://customer-assets.emergentagent.com/job_9a57b70a-7fda-4e0f-8fd3-cbc57f5e99da/artifacts/euccdyev_Download%20300x168.png" },
  { name: "TUI Musement", src: "https://customer-assets.emergentagent.com/job_9a57b70a-7fda-4e0f-8fd3-cbc57f5e99da/artifacts/qa5zbbm9_Download%20474x106.png" },
  { name: "Travelife", src: "https://customer-assets.emergentagent.com/job_9a57b70a-7fda-4e0f-8fd3-cbc57f5e99da/artifacts/smw07n0z_Image%20290x174.jpeg" },
];

const inputClass = "bg-white border-[#0D1B2A]/10 text-[#0D1B2A] placeholder:text-[#0D1B2A]/30 h-11 rounded-lg focus:border-[#174358]/40 focus:ring-1 focus:ring-[#174358]/20";

export default function ServiceLeadPage() {
  const { serviceSlug } = useParams();
  const svc = SERVICE_DATA[serviceSlug];

  const [form, setForm] = useState({ first_name: "", last_name: "", agency_name: "", email: "", phone_code: "+91", phone: "", state: "", comments: "" });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [scrollPos, setScrollPos] = useState(0);

  useEffect(() => { window.scrollTo(0, 0); }, [serviceSlug]);

  if (!svc) return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center gap-4">
      <h2 className="text-2xl font-bold text-[#0D1B2A]">Service not found</h2>
      <Link to="/" className="text-sm text-[#174358] hover:underline">Back to Home</Link>
    </div>
  );

  const isIndia = form.phone_code === "+91";
  const u = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const Icon = svc.icon;
  const scrollLeft = () => setScrollPos((p) => Math.max(0, p - 1));
  const scrollRight = () => setScrollPos((p) => Math.min(testimonials.length - 1, p + 1));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.agency_name || !form.email || !form.phone) {
      toast.error("Please fill in all required fields"); return;
    }
    if (isIndia && !form.state) { toast.error("Please select your state"); return; }
    setLoading(true);
    try {
      // Agent Portal uses dedicated Sembark-integrated endpoint
      if (serviceSlug === "agent-portal") {
        await axios.post(`${API}/agent-portal-lead`, {
          first_name: form.first_name, last_name: form.last_name,
          agency_name: form.agency_name, email: form.email,
          phone_code: form.phone_code, phone: form.phone,
          state: form.state || "", comments: form.comments || "",
        });
      } else {
        await axios.post(`${API}/inquiries`, {
          first_name: form.first_name, last_name: form.last_name,
          agency_name: form.agency_name + (form.comments ? " | Notes: " + form.comments : ""),
          email: form.email, phone_code: form.phone_code, phone: form.phone, state: form.state || "",
          source_page: `Service - ${svc.title}`,
        });
      }
      setSubmitted(true);
      window.history.pushState({}, "", `/services/${serviceSlug}/thank-you`);
      toast.success("Inquiry submitted!");
    } catch { toast.error("Something went wrong."); }
    finally { setLoading(false); }
  };

  return (
    <div data-testid="service-lead-page" className="min-h-screen bg-white">
      {/* Compact Header */}
      <div className="bg-[#0D1B2A] px-6 md:px-12 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <a href="/"><img src="/hidmc-logo-white.png" alt="Hi DMC" className="h-9" /></a>
          <div className="hidden sm:flex items-center gap-4">
            <a href="tel:+911244600000" className="flex items-center gap-1.5 text-sm font-bold text-white/70 hover:text-white transition-colors"><Phone size={14} /> +91 124 4600000</a>
            <span className="text-white/20">|</span>
            <a href="mailto:sales@hidmc.com" className="flex items-center gap-1.5 text-sm font-bold text-white/70 hover:text-white transition-colors"><Mail size={14} /> sales@hidmc.com</a>
          </div>
        </div>
      </div>

      {/* Hero + Form */}
      <section data-testid="service-hero" className="py-12 md:py-16 px-6 md:px-12 lg:px-24 bg-gradient-to-b from-[#0D1B2A] to-[#174358]">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            {/* Left — Copy */}
            <div className="pt-4">
              <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center mb-6">
                <Icon size={24} className="text-white/70" />
              </div>
              <h1 data-testid="service-title" className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl font-light text-white leading-[1.1] mb-5 whitespace-pre-line">
                {svc.headline}
              </h1>
              <p className="text-white/45 text-base leading-relaxed mb-8">{svc.subheadline}</p>
              <div className="space-y-3 mb-8">
                {svc.benefits.map((b) => (
                  <div key={b} className="flex items-start gap-2.5">
                    <Check size={14} className="text-white/50 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-white/55 leading-relaxed">{b}</span>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-6 pt-6 border-t border-white/10">
                {svc.stats.map((s, i) => (
                  <div key={s.label} className="flex items-center gap-6">
                    {i > 0 && <div className="w-px h-10 bg-white/10" />}
                    <div>
                      <p className="text-3xl font-['Cormorant_Garamond'] font-light text-white">{s.val}</p>
                      <p className="text-[10px] text-white/30 uppercase tracking-wider font-semibold">{s.label}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right — Form */}
            <div>
              {submitted ? (
                <div data-testid="service-success" className="bg-white rounded-2xl p-10 text-center shadow-2xl">
                  <div className="w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-5">
                    <Check size={28} className="text-emerald-500" />
                  </div>
                  <h3 className="font-['Cormorant_Garamond'] text-2xl text-[#0D1B2A] mb-3">Thank You!</h3>
                  <p className="text-[#0D1B2A]/50 text-sm leading-relaxed">
                    Our {svc.title} team will get back to you within 24–48 hours with a detailed proposal.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-testid="service-form" className="bg-white rounded-2xl p-7 space-y-4 shadow-2xl">
                  <div className="mb-1">
                    <h3 className="font-['Cormorant_Garamond'] text-xl text-[#0D1B2A] mb-0.5">{svc.formTitle}</h3>
                    <p className="text-[11px] text-[#0D1B2A]/40">{svc.formSub}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-[11px] text-[#0D1B2A]/50 font-semibold uppercase tracking-wider mb-1.5 block">First Name *</label>
                      <Input data-testid="svc-first-name" placeholder="First name" value={form.first_name} onChange={(e) => u("first_name", e.target.value)} className={inputClass} />
                    </div>
                    <div>
                      <label className="text-[11px] text-[#0D1B2A]/50 font-semibold uppercase tracking-wider mb-1.5 block">Last Name *</label>
                      <Input data-testid="svc-last-name" placeholder="Last name" value={form.last_name} onChange={(e) => u("last_name", e.target.value)} className={inputClass} />
                    </div>
                  </div>
                  <div>
                    <label className="text-[11px] text-[#0D1B2A]/50 font-semibold uppercase tracking-wider mb-1.5 block">Agency Name *</label>
                    <Input data-testid="svc-agency" placeholder="Your travel agency" value={form.agency_name} onChange={(e) => u("agency_name", e.target.value)} className={inputClass} />
                  </div>
                  <div>
                    <label className="text-[11px] text-[#0D1B2A]/50 font-semibold uppercase tracking-wider mb-1.5 block">Phone Number *</label>
                    <div className="flex gap-2">
                      <Select value={form.phone_code} onValueChange={(v) => { u("phone_code", v); if (v !== "+91") u("state", ""); }}>
                        <SelectTrigger className="bg-white border-[#0D1B2A]/10 h-11 rounded-lg w-[130px] flex-shrink-0"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-white max-h-[250px]">
                          {phoneCodes.map((pc) => <SelectItem key={pc.code} value={pc.code}><span className="mr-2">{pc.flag}</span> {pc.code}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Input data-testid="svc-phone" type="tel" placeholder="Phone number" value={form.phone} onChange={(e) => u("phone", e.target.value)} className={`${inputClass} flex-1`} />
                    </div>
                  </div>
                  {isIndia && (
                    <div>
                      <label className="text-[11px] text-[#0D1B2A]/50 font-semibold uppercase tracking-wider mb-1.5 block">State *</label>
                      <Select value={form.state} onValueChange={(v) => u("state", v)}>
                        <SelectTrigger className="bg-white border-[#0D1B2A]/10 h-11 rounded-lg w-full"><SelectValue placeholder="Select your state" /></SelectTrigger>
                        <SelectContent className="bg-white max-h-[250px]">{indianStates.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  )}
                  <div>
                    <label className="text-[11px] text-[#0D1B2A]/50 font-semibold uppercase tracking-wider mb-1.5 block">Email *</label>
                    <Input data-testid="svc-email" type="email" placeholder="email@agency.com" value={form.email} onChange={(e) => u("email", e.target.value)} className={inputClass} />
                  </div>
                  <div>
                    <label className="text-[11px] text-[#0D1B2A]/50 font-semibold uppercase tracking-wider mb-1.5 block">Requirements</label>
                    <textarea data-testid="svc-comments" value={form.comments} onChange={(e) => u("comments", e.target.value)}
                      placeholder={`Tell us about your ${svc.title.toLowerCase()} requirements`} rows={3}
                      className="w-full px-3 py-2.5 rounded-lg border border-[#0D1B2A]/10 text-sm text-[#0D1B2A] placeholder:text-[#0D1B2A]/30 focus:outline-none focus:border-[#174358]/40 resize-none bg-white" />
                  </div>
                  <Button type="submit" data-testid="svc-submit" disabled={loading}
                    className="w-full bg-[#174358] text-white hover:bg-[#1e5a75] rounded-full py-5 text-sm font-semibold shadow-md transition-all disabled:opacity-50">
                    {loading ? "Submitting..." : (<>Enquire About {svc.title} <ArrowRight size={14} className="ml-1.5" /></>)}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Partner Logos Strip */}
      <section data-testid="service-partners" className="bg-white py-8 px-6 border-b border-[#0D1B2A]/05">
        <div className="max-w-6xl mx-auto">
          <p className="text-[10px] tracking-[0.25em] uppercase font-semibold text-[#0D1B2A]/20 text-center mb-6">Trusted Supply Partners</p>
          <div className="flex items-center justify-center gap-8 md:gap-12 flex-wrap">
            {partnerLogos.map((p) => (
              <div key={p.name} className="flex items-center justify-center grayscale opacity-40 hover:grayscale-0 hover:opacity-100 transition-all duration-300">
                <img src={p.src} alt={p.name} className="h-8 md:h-10 w-auto object-contain" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Scroller */}
      <section data-testid="service-testimonials" className="py-14 md:py-20 px-6 md:px-12 lg:px-24 bg-[#FAFAFA]">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-end justify-between mb-10">
            <div>
              <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#174358]/30 mb-2">What Agents Say</p>
              <h2 className="font-['Cormorant_Garamond'] text-3xl md:text-4xl font-light text-[#0D1B2A]">
                Trusted by <em className="text-[#174358]/50">850+ Travel Agents</em>
              </h2>
            </div>
            <div className="hidden md:flex items-center gap-2">
              <button data-testid="svc-testimonial-prev" onClick={scrollLeft} disabled={scrollPos === 0}
                className="w-10 h-10 rounded-full border border-[#0D1B2A]/10 flex items-center justify-center hover:bg-[#174358]/05 transition-colors disabled:opacity-30">
                <ChevronLeft size={18} className="text-[#0D1B2A]/50" />
              </button>
              <button data-testid="svc-testimonial-next" onClick={scrollRight} disabled={scrollPos >= testimonials.length - 3}
                className="w-10 h-10 rounded-full border border-[#0D1B2A]/10 flex items-center justify-center hover:bg-[#174358]/05 transition-colors disabled:opacity-30">
                <ChevronRight size={18} className="text-[#0D1B2A]/50" />
              </button>
            </div>
          </div>

          <div className="overflow-hidden">
            <div
              className="flex gap-5 transition-transform duration-500 ease-out"
              style={{ transform: `translateX(-${scrollPos * (100 / 3)}%)` }}
            >
              {testimonials.map((t) => (
                <div key={t.name} data-testid={`svc-testimonial-${t.name.toLowerCase().replace(/\s+/g, '-')}`}
                  className="min-w-[calc(33.333%-14px)] bg-white rounded-xl p-6 border border-[#0D1B2A]/06 hover:border-[#174358]/15 hover:shadow-md transition-all flex-shrink-0 hidden md:block">
                  <div className="flex gap-0.5 mb-3">
                    {Array.from({ length: t.rating }).map((_, j) => (
                      <Star key={j} size={13} fill="#174358" className="text-[#174358]/80" />
                    ))}
                  </div>
                  <p className="text-sm text-[#0D1B2A]/55 leading-relaxed mb-5 line-clamp-4">"{t.quote}"</p>
                  <div className="border-t border-[#0D1B2A]/05 pt-4">
                    <p className="text-sm font-bold text-[#0D1B2A]">{t.name}</p>
                    <p className="text-[11px] text-[#0D1B2A]/35">{t.agency}, {t.city}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Mobile: stack cards */}
            <div className="md:hidden space-y-4">
              {testimonials.slice(0, 4).map((t) => (
                <div key={t.name} className="bg-white rounded-xl p-5 border border-[#0D1B2A]/06">
                  <div className="flex gap-0.5 mb-2">
                    {Array.from({ length: t.rating }).map((_, j) => (
                      <Star key={j} size={12} fill="#174358" className="text-[#174358]/80" />
                    ))}
                  </div>
                  <p className="text-sm text-[#0D1B2A]/55 leading-relaxed mb-4">"{t.quote}"</p>
                  <p className="text-sm font-bold text-[#0D1B2A]">{t.name}</p>
                  <p className="text-[11px] text-[#0D1B2A]/35">{t.agency}, {t.city}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
